import type { RefObject } from "react";
import { t } from "@/i18n-js/instance";
import { UserImageWithFallback } from "@/react/components/shared/uikit/UserImageWithFallback";
import type { Comment as CommentType } from "@/react/types/Comment";
import type { CommunityMember } from "@/react/types/CommunityMember";
import { TiptapCommentBox } from "@circle-react/components/CommentsV3/CommentForm/TiptapCommentBox";
import {
  useCurrentPostContext,
  usePunditUserContext,
} from "@circle-react/contexts";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { usePersistedFormFieldValues } from "@circle-react/hooks/utils/usePersistFormFields";
import { useScrollIntoView } from "@circle-react/hooks/utils/useScrollIntoView";
import { Form } from "@circle-react-uikit/Form";
import { useCommentForm } from "./useCommentForm";

export interface CommentFormProps {
  autoFocus?: boolean;
  bodyInputRef?: RefObject<any>;
  className?: string;
  communityMemberForReply?: CommunityMember | null;
  onCreate: (comment: CommentType) => void;
  parentCommentId?: number;
  persistCacheKey?: string;
  postId: number;
  spaceId: number;
}

export const CommentForm = ({
  spaceId,
  postId,
  parentCommentId,
  communityMemberForReply,
  autoFocus = false,
  onCreate,
  bodyInputRef,
  className,
  persistCacheKey,
}: CommentFormProps) => {
  const divRef = useScrollIntoView<HTMLDivElement>(!!parentCommentId);
  const { currentCommunityMember } = usePunditUserContext();

  const { usedIn, isUsedInMinimalTipTapViewModal } = useCurrentPostContext();
  const shouldShowUserImage = usedIn !== "full-post-modal";

  const { getPersistedFormFieldValues } = usePersistedFormFieldValues({
    cacheKey: persistCacheKey,
  });

  const persistedFormValues = getPersistedFormFieldValues();

  const editorPlaceholder = communityMemberForReply
    ? t("comments.form.fields.body_reply_placeholder", {
        name: communityMemberForReply.name,
      })
    : t("comments.form.fields.body_placeholder");

  const { createComment } = useCommentForm({
    postId,
    parentCommentId,
    onCreate,
  });

  return (
    <div
      className={classNames(
        "post__reply flex items-start gap-3 py-2.5 md:py-5",
        {
          "md:px-6": usedIn !== "minimal-post-modal",
          "reply-to-parent -ml-8 py-2.5 md:ml-0":
            parentCommentId && usedIn !== "full-post-modal",
          "reply-to-parent -ml-8 md:ml-[-3.125rem]":
            parentCommentId && usedIn === "full-post-modal",
          "reply-to-post bg-primary sticky bottom-0": !parentCommentId,
          "md:rounded-b-2xl": !isUsedInMinimalTipTapViewModal,
          "px-5": !parentCommentId && !isUsedInMinimalTipTapViewModal,
        },
      )}
      ref={divRef}
    >
      {shouldShowUserImage && (
        <UserImageWithFallback
          src={currentCommunityMember?.avatar_url}
          name={currentCommunityMember?.name || ""}
          size="10"
        />
      )}
      <Form
        className="relative w-full"
        defaultValues={{
          tiptap_body: persistedFormValues?.tiptap_body,
        }}
        onSubmit={(formData: any) => createComment(formData)}
      >
        <TiptapCommentBox
          autoFocus={autoFocus}
          className={className}
          communityMemberForReply={communityMemberForReply}
          editorRef={bodyInputRef}
          isEdit={false}
          parentCommentId={parentCommentId}
          placeholder={editorPlaceholder}
          postId={postId}
          spaceId={spaceId}
          usedIn={usedIn}
          persistCacheKey={persistCacheKey}
        />
      </Form>
    </div>
  );
};
